import { CalendarDate } from '@internationalized/date';
import { type DateValue } from 'reka-ui';

export function useDateFormat() {
    const formatDate = (date: DateValue): string => {
        if (!date) return '';

        const calendarDate = new CalendarDate(date.year, date.month, date.day);
        return calendarDate.toString();
    };

    const formatDMY = (date: DateValue): string => {
        if (!date) return '';
        return `${date.day}/${String(date.month).padStart(2, '0')}/${date.year}`;
    };

    const parseDateString = (dateString: string): CalendarDate | null => {
        if (!dateString) return null;

        dateString = dateString.split(' ')[0];

        // Handle "yyyy-mm-dd" format
        if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
            const [year, month, day] = dateString.split('-').map(Number);
            try {
                return new CalendarDate(year, month, day);
            } catch (error) {
                return null;
            }
        }

        // Handle "dd-mm-yyyy" format
        if (/^\d{2}-\d{2}-\d{4}$/.test(dateString)) {
            const [day, month, year] = dateString.split('-').map(Number);
            try {
                return new CalendarDate(year, month, day);
            } catch (error) {
                return null;
            }
        }

        // Handle "yyyy/mm/dd" format
        if (/^\d{4}\/\d{2}\/\d{2}$/.test(dateString)) {
            const [year, month, day] = dateString.split('/').map(Number);
            try {
                return new CalendarDate(year, month, day);
            } catch (error) {
                return null;
            }
        }

        // Handle "dd/mm/yyyy" format
        if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateString)) {
            const [day, month, year] = dateString.split('/').map(Number);
            try {
                return new CalendarDate(year, month, day);
            } catch (error) {
                return null;
            }
        }

        // Handle "yyyy-mm-ddThh:mm:ss" format
        if (/^\d{4}-\d{2}-\d{2}T/.test(dateString)) {
            const [datePart] = dateString.split('T');
            const [year, month, day] = datePart.split('-').map(Number);
            try {
                return new CalendarDate(year, month, day);
            } catch (error) {
                return null;
            }
        }

        return null;
    };

    const parseSettlementPeriod = (
        settlementPeriod: string,
    ): { from: CalendarDate; to: CalendarDate } | null => {
        if (!settlementPeriod) return null;

        // Parse format: "01-02-2017-28-02-2017"
        const parts = settlementPeriod.split('-');
        if (parts.length !== 6) return null;

        const [fromDay, fromMonth, fromYear, toDay, toMonth, toYear] = parts.map(Number);

        // Validate that all parts are valid numbers
        if ([fromDay, fromMonth, fromYear, toDay, toMonth, toYear].some(isNaN)) {
            return null;
        }

        try {
            const fromDate = new CalendarDate(fromYear, fromMonth, fromDay);
            const toDate = new CalendarDate(toYear, toMonth, toDay);

            return { from: fromDate, to: toDate };
        } catch (error) {
            // Invalid date values
            return null;
        }
    };

    const formatDMYFromIso = (isoDate: string): string => {
        if (!isoDate) return '';
        return new Date(isoDate).toLocaleDateString('da-DK', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
        });
    };

    // const formatDMYFromIso = (isoDate: string): string => {
    //     if (!isoDate) return '';
    //     const d = new Date(isoDate);
    //     // Get the date components in UTC
    //     const day = d.getUTCDate().toString().padStart(2, '0');
    //     const month = (d.getUTCMonth() + 1).toString().padStart(2, '0'); // months are 0-based
    //     const year = d.getUTCFullYear();

    //     return `${day}.${month}.${year}`;
    // };

    const formatDMYHSFromIso = (isoDate: string): string => {
        if (!isoDate) return '';
        return new Date(isoDate).toLocaleDateString('da-DK', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    };

    function formatDateForRegex(dateString: string, separator = '/') {
        const [year, month, day] = dateString.split('-');
        return `${day}${separator}${month}${separator}${year}`;
    }

    return {
        formatDate,
        formatDMY,
        parseDateString,
        parseSettlementPeriod,
        formatDMYFromIso,
        formatDMYHSFromIso,
        formatDateForRegex,
    };
}
